import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DisplayAPIsException from "../../BasicComponents/DisplayAPIsExceptionAlert";
import { logEvent, logException } from "../../helper/AppInsightsHelper";
import { CustomShimmer } from "../../helper/CustomShimmer";
import { useGetCRMCustomerProductForTenantQuery } from "../../slices/ExploreCustomers.slice";
import CRMCustomerProductCharts from "./CRMCustomerProductCharts";
import CRMCustomerProductTable from "./CRMCustomerProductTable";

const CRMCustomerProductContainer = (props: any) => {
  // Properties
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get("id");
  const { data, error, isError, isFetching, isSuccess } = useGetCRMCustomerProductForTenantQuery(tenantId);
  const [filteredTableItems, setFilteredTableItems] = useState([]);

  // Change: chartFilter as array to handle multiple filters
  const [chartFilters, setChartFilters] = useState<
    Array<{
      filterKey: string;
      value: string;
    }>
  >([]);

  const displayAPIException = (isError && <DisplayAPIsException error={error} height={250} />) || "";

  // Evetns
  const showNoData = () => {
    const rootStyle = {
      alignItems: "center",
      display: "flex",
      fontSize: 18,
      fontWeight: 600,
      justifyContent: "center",
      padding: 200
    };
    return <div style={rootStyle}>{`Customer product details are not available for ${props.tenantName}.`}</div>;
  };

  const customProps = {
    tenantId: tenantId,
    tenantName: props?.tenantName
  };

  // Hooks
  useEffect(() => {
    logEvent(
      "Customer Product Tab - Customer Engagements",
      "Customer Page",
      "CRMCustomerProductContainer",
      customProps
    );
  });

  useEffect(() => {
    setFilteredTableItems(data?.table?.crmCustomerProductList ?? []);
    if (isError) {
      logException(
        "Customer Page - Customer Engagements Tab - Customer Product Sub Tab",
        "CRMCustomerProductContainer",
        "useGetCRMCustomerProductForTenantQuery",
        error,
        customProps
      );
    }
  }, [data, isError]);

  useEffect(() => {
    const filteredData = (data?.table?.crmCustomerProductList || []).filter(
      ({ customerProductState, deploymentStatus, productName }: any) => {
        // If no filters, show all data
        if (chartFilters.length === 0) {
          return true;
        }

        // Apply all filters - item must match ALL active filters
        return chartFilters.every((filter) => {
          if (filter.filterKey === "productName") {
            return productName === filter.value;
          } else if (filter.filterKey === "deploymentStatus") {
            return deploymentStatus === filter.value;
          } else if (filter.filterKey === "customerProductState") {
            return customerProductState === filter.value;
          }
          return true;
        });
      }
    );

    setFilteredTableItems(filteredData);
  }, [chartFilters, data]);

  // Handler to manage array of filters
  const handleChartFilter = (newFilter: { filterKey: string; value: string }) => {
    setChartFilters((prevFilters) => {
      // If filterKey or value is empty, reset all filters
      if (!newFilter.filterKey || !newFilter.value) {
        return [];
      }

      // Check if filter for this key already exists
      const existingFilterIndex = prevFilters.findIndex((f) => f.filterKey === newFilter.filterKey);

      if (existingFilterIndex === -1) {
        // Add new filter
        return [...prevFilters, newFilter];
      } else {
        // Update existing filter
        const updatedFilters = [...prevFilters];
        updatedFilters[existingFilterIndex] = newFilter;
        return updatedFilters;
      }
    });
  };

  return (
    <>
      <CRMCustomerProductCharts
        loading={isFetching}
        onSelectedChartFilter={handleChartFilter}
        options={{
          filteredData: filteredTableItems,
          originalData: data?.table?.crmCustomerProductList || [],
          chartOptions: [
            {
              id: 1,
              field: "productName",
              key: "customer-view-customer-engagements-customer-product-chart-by-productName",
              title: "Product Name",
              type: "donut",
              kustoLink: data?.table?.productKustoLink
            },
            {
              id: 2,
              field: "deploymentStatus",
              key: "customer-view-customer-engagements-customer-product-chart-by-deploymentStatus",
              title: "Deployment Status",
              type: "donut",
              kustoLink: data?.table?.deploymentStatusKustoLink
            },
            {
              id: 3,
              field: "customerProductState",
              key: "customer-view-customer-engagements-customer-product-chart-by-customerProductState",
              title: "State",
              type: "donut",
              kustoLink: data?.table?.stateKustoLink
            }
          ]
        }}
      />
      {isFetching && !filteredTableItems?.length ? (
        <CustomShimmer columnCount={11} />
      ) : (
        (isSuccess && Array.isArray(filteredTableItems) && filteredTableItems.length && (
          <CRMCustomerProductTable
            tableData={filteredTableItems}
            tenantName={props.tenantName}
            crmCustomerProductKustoLink={data?.table?.crmCustomerProductListKustoLink}
          />
        )) ||
        (isSuccess && Array.isArray(filteredTableItems) && filteredTableItems.length === 0 && showNoData())
      )}
      {displayAPIException}
    </>
  );
};

export default CRMCustomerProductContainer;
